/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `AdditionalContact` for the Caintra Connect frontend.
 * @module components/Sections/ContactsData/AdditionalContact
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { ReactComponent as PlusIcon } from "../../../assets/icons/PlusIcon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/icons/ContactsDataSection/TrashIcon.svg";
import {
  AdditionalContactIdentityFields,
  AdditionalContactMotherLastNameField,
} from "./AdditionalContactIdentityFields";
import { AdditionalContactPhoneFields } from "./AdditionalContactPhoneFields";
import { AdditionalContactProfileFields } from "./AdditionalContactProfileFields";
import styles from "./ContactsData.module.css";
import { useAdditionalContactForm } from "./useAdditionalContactForm";
import { CONTACTS_LIMIT_MESSAGE_KEY } from "./additionalContactConstants";

const AdditionalContact = ({
  contact,
  index,
  onRemove,
  isLast,
  onAdd,
  canAdd,
  onContactChange,
  titleOptions,
  countryCodeOptions,
  genderOptions,
  areaOptions,
  getFieldValidationState,
  getErrorMessage,
}) => {
  const { t } = useTranslation();
  const isFirst = index === 0;
  const form = useAdditionalContactForm({ contact, index, onContactChange });
  const fieldProps = {
    t,
    index,
    contact,
    form,
    getFieldValidationState,
    getErrorMessage,
  };

  return (
    <div className={styles.additionalContactSection}>
      <div className={styles.sectionHeader}>
        <span className={styles.mainContactLabel}>
          {t("components.contactsData.additionalContactLabel")} {index + 1}
        </span>
        {!isFirst && (
          <Button
            className={styles.deleteContactButton}
            onClick={() => onRemove(index)}
            type="button"
          >
            <TrashIcon className={styles.trashIcon} />
            {t("components.contactsData.deleteContactButton")}
          </Button>
        )}
      </div>
      <AdditionalContactIdentityFields
        {...fieldProps}
        titleOptions={titleOptions}
      />
      <div className={styles.inputRow}>
        <AdditionalContactMotherLastNameField {...fieldProps} />
        <AdditionalContactPhoneFields
          {...fieldProps}
          countryCodeOptions={countryCodeOptions}
        />
      </div>
      <AdditionalContactProfileFields
        {...fieldProps}
        genderOptions={genderOptions}
        areaOptions={areaOptions}
      />

      {/* Add Contact Button - Only show on the last section if we can add more contacts */}
      {isLast && canAdd && (
        <>
          {/* Contact limit message */}
          <div className={styles.contactsLimitMessage}>
            *{t(CONTACTS_LIMIT_MESSAGE_KEY)}
          </div>

          {/* Tablet contacts limit message */}
          <div className={styles.contactsLimitMessageTablet}>
            {t(CONTACTS_LIMIT_MESSAGE_KEY)}
          </div>

          {/* Mobile contacts limit message */}
          <div className={styles.contactsLimitMessageMobile}>
            {t(CONTACTS_LIMIT_MESSAGE_KEY)}
          </div>

          <div className={styles.addContactContainer}>
            <button
              className={styles.addContactButton}
              onClick={onAdd}
              type="button"
            >
              <PlusIcon className={styles.plusIcon} />
              {t("components.contactsData.addContactButton")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdditionalContact;
